import React, { useState, useEffect, useRef } from 'react';
import { Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import {
  getDictTableMeta,
  getFkCompanionTemplate,
  saveFkCompanionTemplate,
  type DictTableMetaColumn,
  type FkFieldMapping,
  type ColumnSuggestion,
} from '../../services/data-integration-api';

const PERSON_DEFAULTS: FkFieldMapping[] = [
  { suffix: '_name', label: '姓名', ref_column: '', enabled: true, data_type: 'varchar', data_length: 64 },
  { suffix: '_phone', label: '手机号码', ref_column: '', enabled: true, data_type: 'varchar', data_length: 64 },
  { suffix: '_employee_no', label: '工号', ref_column: '', enabled: true, data_type: 'varchar', data_length: 64 },
];

const DEPARTMENT_DEFAULTS: FkFieldMapping[] = [
  { suffix: '_dept_name', label: '部门名称', ref_column: '', enabled: true, data_type: 'varchar', data_length: 64 },
  { suffix: '_dept_abbr', label: '部门简称', ref_column: '', enabled: true, data_type: 'varchar', data_length: 64 },
];

interface FkCompanionConfigPanelProps {
  targetColumnName: string;
  sourceColumnName: string;
  sourceConnectionId: string;
  catalogTables: Array<{ table_name: string; table_comment: string }>;
  fkType: 'person' | 'department';
  config: {
    fk_ref_table: string;
    fk_ref_pk_column: string;
    fk_field_mappings: FkFieldMapping[];
  };
  onChange: (field: string, value: any) => void;
  onCompanionColumnsChange: (companions: ColumnSuggestion[]) => void;
}

/** 检查 FK 配置是否完整 */
export function isFkConfigComplete(config: {
  fk_ref_table: string;
  fk_ref_pk_column: string;
  fk_field_mappings: FkFieldMapping[];
}): boolean {
  if (!config.fk_ref_table || !config.fk_ref_pk_column) return false;
  const enabledMappings = config.fk_field_mappings.filter(m => m.enabled);
  if (enabledMappings.length === 0) return false;
  return enabledMappings.every(m => !!m.ref_column);
}

export default function FkCompanionConfigPanel({
  targetColumnName,
  sourceColumnName,
  sourceConnectionId,
  catalogTables,
  fkType,
  config,
  onChange,
  onCompanionColumnsChange,
}: FkCompanionConfigPanelProps) {
  const [columns, setColumns] = useState<DictTableMetaColumn[]>([]);
  const [loadingCols, setLoadingCols] = useState(false);
  const [templateLoaded, setTemplateLoaded] = useState(false);
  const prevCompleteRef = useRef(false);

  const isComplete = isFkConfigComplete(config);
  const title = fkType === 'person' ? '人员外键配置' : '机构部门外键配置';

  // 初始化默认 field_mappings（如果为空）
  useEffect(() => {
    if (config.fk_field_mappings.length === 0) {
      const defaults = fkType === 'person' ? PERSON_DEFAULTS : DEPARTMENT_DEFAULTS;
      onChange('fk_field_mappings', defaults.map(d => ({ ...d })));
    }
  }, [fkType]);

  // 加载复用模板（仅首次挂载且配置为空时）
  useEffect(() => {
    if (templateLoaded || !sourceConnectionId) return;
    if (config.fk_ref_table) {
      setTemplateLoaded(true);
      return;
    }

    (async () => {
      try {
        const result = await getFkCompanionTemplate({ connectionId: sourceConnectionId, fkType });
        if (result.template) {
          const t = result.template;
          onChange('fk_ref_table', t.ref_table_name);
          onChange('fk_ref_pk_column', t.ref_pk_column);
          onChange('fk_field_mappings', t.field_mappings);
          toast.info('已从复用模板自动填充外键配置');
        }
      } catch {
        // 无模板，静默
      } finally {
        setTemplateLoaded(true);
      }
    })();
  }, [sourceConnectionId, fkType, templateLoaded]);

  // 当关联表变更时，加载列信息
  useEffect(() => {
    if (!config.fk_ref_table || !sourceConnectionId) {
      setColumns([]);
      return;
    }
    (async () => {
      setLoadingCols(true);
      try {
        const result = await getDictTableMeta({
          connectionId: sourceConnectionId,
          tableName: config.fk_ref_table,
        });
        setColumns(result.columns);
      } catch {
        setColumns([]);
      } finally {
        setLoadingCols(false);
      }
    })();
  }, [config.fk_ref_table, sourceConnectionId]);

  // 配置完成时生成伴生列 + 保存模板
  useEffect(() => {
    if (isComplete && !prevCompleteRef.current) {
      toast.success('外键配置已完成');
      // 生成伴生列
      generateCompanionColumns();
      // 保存模板
      saveTemplate();
    }
    prevCompleteRef.current = isComplete;
  }, [isComplete]);

  // 配置变更时更新伴生列（仅在已完整时）
  useEffect(() => {
    if (isComplete) {
      generateCompanionColumns();
    }
  }, [
    config.fk_field_mappings.map(m => `${m.enabled}:${m.ref_column}:${m.suffix}`).join(','),
    targetColumnName,
  ]);

  const generateCompanionColumns = () => {
    const companions: ColumnSuggestion[] = config.fk_field_mappings
      .filter(m => m.enabled && m.ref_column)
      .map(m => {
        let companionName = `${targetColumnName}${m.suffix}`;
        // 列名长度限制
        if (companionName.length > 64) {
          companionName = companionName.substring(0, 64);
          toast.warning(`伴生列名 "${companionName}" 已截断至64字符`);
        }
        return {
          source_column_name: `__fk_${sourceColumnName}${m.suffix}`,
          source_data_type: '',
          target_column_name: companionName,
          target_data_type: m.data_type,
          target_column_length: m.data_length,
          target_is_nullable: true,
          target_default_value: null,
          target_is_index: false,
          target_column_comment: m.label,
          transform_rule: 'direct' as const,
          mapping_status: 'auto' as const,
          suggestion_reason: `FK伴生列: ${config.fk_ref_table}.${m.ref_column}`,
          fk_companion_of: sourceColumnName,
          fk_ref_value_column: m.ref_column,
          fk_companion_suffix: m.suffix,
        };
      });
    onCompanionColumnsChange(companions);
  };

  const saveTemplate = async () => {
    try {
      await saveFkCompanionTemplate({
        connectionId: sourceConnectionId,
        fkType,
        refTableName: config.fk_ref_table,
        refPkColumn: config.fk_ref_pk_column,
        fieldMappings: config.fk_field_mappings,
      });
      toast.info('外键模板已保存');
    } catch {
      // 模板保存失败不阻塞主流程
    }
  };

  const updateFieldMapping = (index: number, field: keyof FkFieldMapping, value: any) => {
    const updated = config.fk_field_mappings.map((m, i) =>
      i === index ? { ...m, [field]: value } : m
    );
    onChange('fk_field_mappings', updated);
  };

  const columnOptions = columns.map(c => ({
    value: c.column_name,
    label: c.column_name,
  }));

  const tableOptions = catalogTables.map(t => ({
    value: t.table_name,
    label: t.table_name + (t.table_comment ? ` (${t.table_comment})` : ''),
  }));

  return (
    <Card className="border-l-4 border-l-[var(--color-accent-blue)]">
      <CardHeader className="flex flex-row items-center justify-between py-2 px-4 space-y-0">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Link2 className="h-4 w-4 text-[var(--color-accent-blue)]" />
          {title} — {targetColumnName}
        </div>
        <Badge className={
          isComplete
            ? 'bg-[var(--color-success-light)] text-[var(--color-success)]'
            : 'bg-[var(--color-warning-light)] text-[var(--color-warning)]'
        }>
          {isComplete ? '已配置' : '待完善'}
        </Badge>
      </CardHeader>
      <CardContent className="px-4 pb-3 pt-0">
        <div className="grid grid-cols-3 gap-x-4 gap-y-3">
          {/* 关联表（占两列） */}
          <div className="col-span-2 space-y-1">
            <Label className="text-xs">关联表 <span className="text-red-500">*</span></Label>
            <Combobox
              value={config.fk_ref_table}
              onChange={v => onChange('fk_ref_table', v)}
              options={tableOptions}
              placeholder="选择关联表..."
              searchPlaceholder="搜索表名..."
            />
          </div>

          {/* 主键列 */}
          <div className="space-y-1">
            <Label className="text-xs">主键列 <span className="text-red-500">*</span></Label>
            <Combobox
              value={config.fk_ref_pk_column}
              onChange={v => onChange('fk_ref_pk_column', v)}
              options={columnOptions}
              placeholder="选择..."
              searchPlaceholder="搜索列名..."
              disabled={loadingCols || columns.length === 0}
            />
          </div>

          {/* 伴生列配置 */}
          <div className="col-span-3">
            <Label className="text-xs mb-2 block">取值字段映射</Label>
            <div className="space-y-2">
              {config.fk_field_mappings.map((fm, idx) => (
                <div key={fm.suffix} className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={fm.enabled}
                    onChange={e => updateFieldMapping(idx, 'enabled', e.target.checked)}
                    className="h-4 w-4"
                  />
                  <span className="text-xs w-20 text-muted-foreground">{fm.label}</span>
                  <Input
                    type="text"
                    value={fm.suffix}
                    onChange={e => updateFieldMapping(idx, 'suffix', e.target.value)}
                    className="h-7 w-32 font-mono text-xs"
                    placeholder="后缀"
                  />
                  <Combobox
                    value={fm.ref_column}
                    onChange={v => updateFieldMapping(idx, 'ref_column', v)}
                    options={columnOptions}
                    placeholder="选择取值字段..."
                    searchPlaceholder="搜索列名..."
                    disabled={!fm.enabled || loadingCols || columns.length === 0}
                    className="flex-1"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
